import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import {
  Button,
  Card,
  Input,
  PageHeader,
  Select,
  Textarea,
  useToast,
} from "../components/ui";
import {
  templateService,
  type ProjectTemplate,
  type TemplateModuleOption,
} from "../services/template.service";
import { projectService, type ProjectVisibility } from "../services/project.service";
import { validationErrors } from "../services/http";
import JourneySteps from "../components/JourneySteps";
import AdvancedModeToggle from "../components/AdvancedModeToggle";
import { useAdvancedMode } from "../lib/plain-language";

const VISIBILITY_OPTIONS = [
  { value: "organization", label: "Minha empresa — todo mundo da minha equipe" },
  { value: "private", label: "Só eu — e os administradores" },
  { value: "public", label: "Qualquer pessoa com o link (depois de publicado)" },
];

export default function NewProjectPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [template, setTemplate] = useState("");
  const [organization, setOrganization] = useState("");
  const [visibility, setVisibility] = useState<ProjectVisibility>("organization");
  const [locale, setLocale] = useState("pt_BR");
  const [timezone, setTimezone] = useState("America/Sao_Paulo");
  const [appPort, setAppPort] = useState("8080");
  const [gitInit, setGitInit] = useState(true);
  const [modules, setModules] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string[]>>({});
  const [saving, setSaving] = useState(false);
  const [advanced] = useAdvancedMode();

  useEffect(() => {
    templateService
      .list()
      .then((list) => {
        setTemplates(list);
        setTemplate(list.find((item) => item.is_default)?.id ?? list[0]?.id ?? "");
      })
      .catch(() => setTemplates([]));
  }, []);

  const selected = templates.find((item) => item.id === template);
  const moduleOptions: TemplateModuleOption[] = selected?.module_options ?? [];

  // Ao trocar de template, começa com os módulos marcados como padrão no manifesto.
  useEffect(() => {
    setModules(moduleOptions.filter((option) => option.default).map((option) => option.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, templates.length]);

  function toggleModule(option: TemplateModuleOption, checked: boolean) {
    setModules((current) => {
      if (checked) {
        // Inclui as dependências declaradas pelo módulo.
        return Array.from(new Set([...current, option.key, ...option.requires]));
      }
      // Remove também quem depende do módulo desmarcado.
      const dependents = moduleOptions
        .filter((item) => item.requires.includes(option.key))
        .map((item) => item.key);
      return current.filter((key) => key !== option.key && !dependents.includes(key));
    });
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const project = await projectService.create({
        name,
        description: description || undefined,
        template: template || undefined,
        organization: organization || undefined,
        visibility,
        modules,
        settings: {
          locale,
          timezone,
          app_port: Number(appPort) || 8080,
          git_init: gitInit,
        },
      });
      toast.success("Sistema criado. Agora conte a sua ideia.");
      navigate(`/projects/${project.id}/builder`);
    } catch (err) {
      setErrors(validationErrors(err));
      toast.error(err instanceof Error ? err.message : "Não foi possível criar o sistema");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Criar um novo sistema"
        description="Dê um nome, conte a sua ideia e a inteligência artificial monta o sistema para você."
        actions={<AdvancedModeToggle />}
      />

      <JourneySteps current={0} className="mb-4" />

      <Card className="max-w-2xl">
        <form onSubmit={submit} className="space-y-5">
          <Input
            label="Nome do sistema"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Ex.: Oficina Mecânica"
            required
            error={errors.name?.[0]}
          />

          <Textarea
            label="O que esse sistema precisa fazer?"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Ex.: sistema com clientes, veículos, ordens de serviço, financeiro e estoque."
            error={errors.description?.[0]}
          />

          <Select
            label="Ponto de partida"
            value={template}
            onChange={(event) => setTemplate(event.target.value)}
            placeholder={templates.length ? undefined : "Nenhum ponto de partida disponível"}
            options={templates.map((item) => ({
              value: item.id,
              label: item.is_default ? `${item.name} (padrão)` : item.name,
            }))}
            error={errors.template?.[0]}
          />

          {moduleOptions.length > 0 && (
            <fieldset className="space-y-3 rounded-lg border border-slate-800 p-4">
              <legend className="px-1 text-sm font-medium text-slate-300">
                O que já vem pronto
              </legend>
              <p className="text-xs text-slate-400">
                Marque o que o seu sistema precisa. Essas partes já vêm prontas e testadas — a
                inteligência artificial continua a partir delas.
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {moduleOptions.map((option) => (
                  <label
                    key={option.key}
                    className="flex gap-2 rounded-md border border-slate-800 p-3 text-sm text-slate-300"
                  >
                    <input
                      type="checkbox"
                      checked={modules.includes(option.key)}
                      onChange={(event) => toggleModule(option, event.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-slate-700 bg-slate-900"
                    />
                    <span>
                      <span className="font-medium text-slate-200">{option.name}</span>
                      <span className="block text-xs text-slate-400">{option.description}</span>
                      {option.requires.length > 0 && (
                        <span className="block text-xs text-slate-500">
                          Precisa de: {option.requires.join(", ")}
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {selected?.description && (
            <p className="-mt-3 text-sm text-slate-400">
              {selected.description}
              {selected.modules?.length ? ` · Módulos previstos: ${selected.modules.join(", ")}.` : ""}
            </p>
          )}

          <Select
            label="Quem pode ver"
            value={visibility}
            onChange={(event) => setVisibility(event.target.value as ProjectVisibility)}
            options={VISIBILITY_OPTIONS}
            error={errors.visibility?.[0]}
          />

          {(user?.organizations?.length ?? 0) > 1 && (
            <Select
              label="Empresa"
              value={organization}
              onChange={(event) => setOrganization(event.target.value)}
              placeholder="Selecionar…"
              options={(user?.organizations ?? []).map((org) => ({
                value: org.id,
                label: org.name,
              }))}
              error={errors.organization?.[0]}
            />
          )}

          {advanced && (
          <fieldset className="space-y-4 rounded-lg border border-slate-800 p-4">
            <legend className="px-1 text-sm font-medium text-slate-300">Ajustes avançados</legend>

            <div className="grid gap-4 sm:grid-cols-3">
              <Select
                label="Idioma"
                value={locale}
                onChange={(event) => setLocale(event.target.value)}
                options={[
                  { value: "pt_BR", label: "Português (Brasil)" },
                  { value: "en", label: "Inglês" },
                  { value: "es", label: "Espanhol" },
                ]}
                error={errors["settings.locale"]?.[0]}
              />
              <Select
                label="Fuso horário"
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                options={[
                  { value: "America/Sao_Paulo", label: "America/Sao_Paulo" },
                  { value: "America/Manaus", label: "America/Manaus" },
                  { value: "UTC", label: "UTC" },
                ]}
                error={errors["settings.timezone"]?.[0]}
              />
              <Input
                label="Porta local"
                type="number"
                value={appPort}
                onChange={(event) => setAppPort(event.target.value)}
                hint="Usada no docker-compose do projeto gerado."
                error={errors["settings.app_port"]?.[0]}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={gitInit}
                onChange={(event) => setGitInit(event.target.checked)}
                className="h-4 w-4 rounded border-slate-700 bg-slate-900"
              />
              Inicializar repositório Git com o primeiro commit
            </label>
          </fieldset>
          )}


          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => navigate("/projects")}>
              Cancelar
            </Button>
            <Button type="submit" loading={saving}>
              Criar sistema
            </Button>
          </div>
        </form>
      </Card>
    </>
  );
}
